import React, { useCallback } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  BoxV4,
  DatePickerV2,
  monthYearWithComaFormat,
  NoteColors,
  NoteV2,
  SelectV4,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { get, isBoolean, isEmpty } from 'lodash'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { Grid, SelectChangeEvent } from '@mui/material'
import { DateTime } from 'luxon'
import { MsftCombinedFormState } from '../../../../modules/microsoftCombinedForm/reducer'
import { useStyles } from '../../../PrivateOfferFormV2/AzureOffer/ContractDuration/ContractDuration.styles'
import {
  CombinedFormFAQs,
  CombinedFormFAQsItem,
} from '../CombinedFormFAQs/CombinedFormFAQs'

const startDateOptions = [
  {
    label: 'Accepted date',
    value: true,
  },
  {
    label: 'Specified month',
    value: false,
  },
]

export const ContractDuration = () => {
  const formikProps = useFormikContext() as FormikProps<MsftCombinedFormState>
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formikProps
  const { classes } = useStyles()
  const agreementStartDateField = {
    title: 'Purchase start month',
    humanReadableKey: 'Purchase start month',
    placeHolder: 'Input',
    id: 'offerStartDate',
    isRequired: false,
    isReadOnly: false,
    isCustom: false,
    dataType: 'date',
    minDate: DateTime.now().plus({ months: 1 }).startOf('month').toISO(),
  }

  const agreementEndDateField = {
    title: 'Purchase end month',
    humanReadableKey: 'Purchase end month',
    placeHolder: 'Input',
    id: 'offerExpirationDate',
    isRequired: false,
    isReadOnly: false,
    isCustom: false,
    dataType: 'date',
    minDate: 'today',
  }

  const handleStartDateChange = useCallback(async (date: string | null) => {
    const updatedDate = date
      ? DateTime.fromISO(date).startOf('month').toISO()
      : ''
    await setFieldValue('draftOffer.offerStartDate', updatedDate)
    await setFieldTouched('draftOffer.offerStartDate', true)
  }, [])

  const handleEndDateChange = useCallback(async (date: string | null) => {
    const updatedDate = date
      ? DateTime.fromISO(date).endOf('month').toISO()
      : ''
    await setFieldValue('draftOffer.offerExpirationDate', updatedDate)
    await setFieldTouched('draftOffer.offerExpirationDate', true)
  }, [])

  const handleChangeStartDateOption = useCallback(
    async (e: SelectChangeEvent) => {
      const value = e.target.value
      await setFieldValue('draftOffer.variableStartDate', value)
      if (value) {
        await setFieldValue('draftOffer.offerStartDate', null)
        await setFieldTouched('draftOffer.offerStartDate', false)
      } else {
        await setFieldValue('draftOffer.offerExpirationDate', null)
        await setFieldTouched('draftOffer.offerExpirationDate', false)
      }
    },
    []
  )

  return (
    <div data-testid="Contract-duration-section">
      <BoxV4
        heading={'Purchase terms'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '16px', height: '16px' }}
          />
        }
        secondaryActions={
          <CombinedFormFAQs item={CombinedFormFAQsItem.PURCHASE_TERMS} />
        }
      >
        <div className={classes.sectionContainer}>
          <NoteV2
            backgroundColor={NoteColors.BLUE}
            description={
              'Set the window during which the buyer can purchase under private pricing. If no purchase is made by the last date of the end month, the offer will expire even if already accepted.'
            }
          />
          <Grid container width={'100%'} spacing={3}>
            <Grid size={6}>
              <SelectV4
                field={{
                  title: 'Purchase start date',
                  placeHolder: 'Purchase start date',
                  humanReadableKey: 'Purchase start date',
                  id: 'draftOffer.variableStartDate',
                  isRequired: false,
                  isReadOnly: false,
                  isCustom: false,
                  defaultValue: 'Accepted date',
                  dataType: 'enum' as DataType,
                  options: startDateOptions,
                }}
                size="small"
                value={values?.draftOffer.variableStartDate}
                onChange={handleChangeStartDateOption}
                onBlur={handleBlur}
                isError={
                  !!(
                    get(touched, 'draftOffer.variableStartDate', false) &&
                    get(errors, 'draftOffer.variableStartDate', false)
                  )
                }
                errorText={
                  (!!get(touched, 'draftOffer.variableStartDate', false) &&
                    get(errors, 'draftOffer.variableStartDate', '')) ||
                  ''
                }
              />
            </Grid>
            {values?.draftOffer.variableStartDate === false && (
              <Grid size={6}>
                <DatePickerV2
                  format={monthYearWithComaFormat}
                  field={{
                    ...agreementStartDateField,
                    defaultValue: values?.draftOffer.offerStartDate as string,
                  }}
                  key={'draftOffer.offerStartDate'}
                  showErrorHelper={
                    !!get(touched, 'draftOffer.offerStartDate', false) &&
                    !isEmpty(get(errors, 'draftOffer.offerStartDate', false))
                  }
                  fieldId={'draftOffer.offerStartDate'}
                  handleDateChange={handleStartDateChange}
                  errorHelperText={
                    (!!get(touched, 'draftOffer.offerStartDate', false) &&
                      get(errors, 'draftOffer.offerStartDate', ' ')) ||
                    ' '
                  }
                  views={['year', 'month']}
                  size="small"
                />
              </Grid>
            )}
            {isBoolean(values?.draftOffer?.variableStartDate) && (
              <Grid size={6}>
                <DatePickerV2
                  format={monthYearWithComaFormat}
                  field={{
                    ...agreementEndDateField,
                    defaultValue: values?.draftOffer
                      .offerExpirationDate as string,
                  }}
                  key={'draftOffer.offerExpirationDate'}
                  showErrorHelper={
                    !!get(touched, 'draftOffer.offerExpirationDate', false) &&
                    !isEmpty(
                      get(errors, 'draftOffer.offerExpirationDate', false)
                    )
                  }
                  fieldId={'draftOffer.offerExpirationDate'}
                  handleDateChange={handleEndDateChange}
                  errorHelperText={
                    (!!get(touched, 'draftOffer.offerExpirationDate', false) &&
                      get(errors, 'draftOffer.offerExpirationDate', ' ')) ||
                    ''
                  }
                  views={['year', 'month']}
                  size="small"
                />
              </Grid>
            )}
          </Grid>
        </div>
      </BoxV4>
    </div>
  )
}
